/**
 * The words a workspace is described in: what a repository is written in and
 * what it serves, how its environment reaches it, the lanes a card moves
 * through, and what a reserved tunnel domain may be (§4.1, §5.7, §5.14, §5.17,
 * §5.21).
 *
 * Here rather than in the desktop app because a project an organization shares
 * is written in them too (§5.25), and the account area has to refuse exactly
 * the values the app refuses.
 */

export type RuntimeKind = 'node' | 'java' | 'python' | 'go' | 'rust' | 'php' | 'static' | 'mixed' | 'unknown';

export const RUNTIME_KINDS: readonly RuntimeKind[] = [
  'node',
  'java',
  'python',
  'go',
  'rust',
  'php',
  'static',
  'mixed',
  'unknown'
];

/**
 * What a project *serves* — a different question from what it is written in
 * (§15). It decides what Preview opens on the project's port: a page gets the
 * browser, an API gets the request console (§5.11).
 *
 * `both` is not a hedge, it is the common case: a Next.js app has route
 * handlers under the pages it renders and a Django site has DRF beside its
 * templates, and one port serves the two. Such a project appears on *both*
 * surfaces — the browser and the console — rather than being made to choose.
 *
 * `unknown` keeps the browser, and it means "there is nothing here to preview"
 * rather than "detection gave up": a repository that is *started* at all and
 * shows no sign of a view layer is called `api` (§15). What is left — a
 * library, a CLI, a monorepo root — has no port to open anything on, so the
 * browser is the harmless answer.
 */
export type ProjectKind = 'ui' | 'api' | 'both' | 'unknown';

export const PROJECT_KINDS: readonly ProjectKind[] = ['ui', 'api', 'both', 'unknown'];

/**
 * The same answer as the two independent questions the settings row asks for.
 *
 * They are independent because one port routinely serves both a page and an API,
 * and `unknown` is neither of them said — not a third state to be picked. Here
 * beside the kind rather than in the app's Preview, because detection asks it
 * too: a readiness probe is proposed only for a project that serves pages, and
 * both surfaces that detect a repository have to propose the same one.
 */
export function kindServes(kind: ProjectKind): { page: boolean; api: boolean } {
  return { page: kind === 'ui' || kind === 'both', api: kind === 'api' || kind === 'both' };
}

export function kindFrom(serves: { page: boolean; api: boolean }): ProjectKind {
  if (serves.page && serves.api) return 'both';
  return serves.page ? 'ui' : serves.api ? 'api' : 'unknown';
}

/**
 * When a variable was injected: install time, start time, or both (§5.7). Legacy:
 * since 2026-09-30 a repository's variables reach install *and* start, always —
 * the owner's call — so every copy now reads and writes `both`. The three words
 * stay accepted because an organization's document written by an older app still
 * carries them, and an older app's parser still requires the key.
 */
export type EnvScope = 'build' | 'runtime' | 'both';

export const ENV_SCOPES: readonly EnvScope[] = ['build', 'runtime', 'both'];

/**
 * Whether this project is given a dotenv file. `auto` is the ecosystem's own
 * convention — what detection concluded from the repository, and what a project
 * saved before this existed falls back to; the other two are the user overruling
 * it in workspace settings (§15).
 */
export type EnvFileMode = 'auto' | 'dotenv' | 'none';

export const ENV_FILE_MODES: readonly EnvFileMode[] = ['auto', 'dotenv', 'none'];

export function isEnvFileMode(value: unknown): value is EnvFileMode {
  return typeof value === 'string' && (ENV_FILE_MODES as readonly string[]).includes(value);
}

/**
 * The colours a workspace and a project are marked with (§4.1, §5.25).
 *
 * One hue each, all at the same chroma, and they mean nothing but *which one*.
 * Kept in the order every picker offers them, and written in the app's own
 * `oklch()` rather than a hex: a workspace published from the account area is
 * marked in the values the app writes, so the picker on either side can show
 * the colour the other chose as the one that is chosen.
 *
 * A document may hold a colour that is not here — anything written before this
 * list, or by hand. It is kept as it is; a picker shows it beside these.
 */
export const WORKSPACE_COLORS: readonly string[] = [
  'oklch(0.52 0.1 205)',
  'oklch(0.55 0.1 300)',
  'oklch(0.52 0.11 150)',
  'oklch(0.62 0.13 60)',
  'oklch(0.55 0.14 25)',
  'oklch(0.52 0.1 265)'
];

/** The colour a row of this rank gets when nothing has chosen one. */
export function workspaceColorAt(index: number): string {
  return WORKSPACE_COLORS[((index % WORKSPACE_COLORS.length) + WORKSPACE_COLORS.length) % WORKSPACE_COLORS.length];
}

/**
 * A shell variable name (§5.7, §5.19). What a project's `portEnv` must be, since
 * it is written into a dotenv file and a shell profile, where a name with a
 * space or an `=` in it would be a line those files cannot mean.
 */
export const SHELL_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * The lanes a task travels through. Two of them are the user's — a task is
 * written in `backlog` and pushed into `todo` when it is ready to be worked on —
 * and three are the board's: it moves a task into `in-development` when it gives
 * it a session, into `done` when the agent hands control back, and into
 * `archived` when the work is merged.
 *
 * `archived` is not a column on the board. It is where a task goes once it has
 * landed, and it is read on its own surface — the board is what is still ahead.
 */
export type TaskLane = 'backlog' | 'todo' | 'in-development' | 'done' | 'archived';

export const TASK_LANES: readonly TaskLane[] = ['backlog', 'todo', 'in-development', 'done', 'archived'];

/** Where imported work comes from (§5.21). */
export type TaskSourceKind = 'jira' | 'trello';

export const TASK_SOURCE_KINDS: readonly TaskSourceKind[] = ['jira', 'trello'];

/** The status each lane stands for, in both directions (§5.21). A lane with none is not synced. */
export type LaneStatuses = Partial<Record<TaskLane, string>>;

/**
 * Why this cannot be a reserved domain, or null when it can (§5.17).
 *
 * A reserved domain is what makes the URL *stable*, and stability is the whole
 * reason the feature is usable for a callback: an Auth0 application is
 * configured with one address, and a session that comes up on a new random
 * subdomain every morning means editing that configuration every morning.
 *
 * A bare hostname, exactly as the egress allowlist takes one (§7.1) — no scheme,
 * no port, no path, no wildcard. The provider is handed it as a URL, so anything
 * that could carry a second meaning into an argument list is refused here rather
 * than quoted somewhere later.
 */
export function tunnelDomainProblem(domain: string): string | null {
  const host = domain.trim().toLowerCase();
  if (!host) return null; // empty is "let the provider assign one" — not an error
  if (host.length > 253) return 'That is too long to be a hostname.';
  if (/^[a-z][a-z0-9+.-]*:\/\//.test(host)) return 'A hostname only — no https:// in front of it.';
  if (host.includes('/')) return 'A hostname only — no path.';
  if (host.includes(':')) return 'A hostname only — no port.';
  if (host.includes('*')) return 'Wildcards are not a domain a tunnel can be reserved on.';
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(host)) {
    return 'That is not a hostname — letters, digits and hyphens, in dotted labels.';
  }
  return null;
}

/** The domain as the provider is asked for it: cleaned, or empty for "any". */
export function normalizeTunnelDomain(domain: string | null | undefined): string {
  const host = (domain ?? '').trim().toLowerCase();
  return tunnelDomainProblem(host) ? '' : host;
}

/**
 * Where a row of a workspace came from, once that workspace is a copy of an
 * organization's project (§5.25). `local` was made here and goes out with the
 * next publish; `cloud` arrived with the project and is what a pull may change;
 * `kept` was made here and stays here — a publish leaves it out and a merge
 * never sees it.
 */
export type RowOrigin = 'local' | 'cloud' | 'kept';

export const ROW_ORIGINS: readonly RowOrigin[] = ['local', 'cloud', 'kept'];
